"use client"

import { graphql, useFragment } from "@/gql"
import { CreditsMetadataSectionFragmentDoc, ProjectMetadataFragment } from "@/gql/graphql"
import { useState } from "react"
import { MetadataSectionContainer } from "./MetadataSectionContainer"
import { MobileJoinNovaOverlay } from "./MobileJoinNovaOverlay"
import { PrivateUsersCard } from "./PrivateUsersCard"
import type { NovaSession } from "@/types"
import { Text } from "@/elements"
import { ContributorsModal } from "./ContributorsModal"
import { CreativeCredit } from "../CreativeCredit"

graphql(`
  fragment CreditsMetadataSection on CreativeWorkPortfolioProject {
    id
    creatorRolesString
    creative {
      id
      name
      avatarInitials
      avatar80w: avatarUrl(width: 80)
      ... on IndividualCreative {
        handle
      }
    }
    privateContributorCount
    contributors(first: 5, onlyPublicProfiles: $onlyPublicProfiles) {
      totalCount
      edges {
        node {
          id
          __typename

          ...CreativeCredit
        }
      }
    }
  }
`)

interface Props {
  project: ProjectMetadataFragment
  mobile?: boolean
  serverSession: NovaSession | null
}

export const CreditsMetadataSection: React.FC<Props> = ({ mobile = false, serverSession, ...props }) => {
  const project = useFragment(CreditsMetadataSectionFragmentDoc, props.project)
  const [showMobileOverlay, setShowMobileOverlay] = useState(false)
  const [viewAllModalOpen, setViewAllModalOpen] = useState(false)

  const isAuthenticated = serverSession?.accessToken
  const creative = project?.creative?.__typename === "IndividualCreative" ? project?.creative : null
  const privateContributorsLength = project?.privateContributorCount || 0
  const hasMoreThanFiveCredits = Boolean(project?.contributors?.totalCount && project?.contributors?.totalCount > 5)

  const contributors = project?.contributors?.edges || []

  return (
    <>
      <ContributorsModal open={viewAllModalOpen} onOpenChange={setViewAllModalOpen} projectId={project.id} />
      <MetadataSectionContainer title="Credits">
        <div className={mobile ? "max-w-[350px]" : ""}>
          <div className="flex flex-col">
            <CreativeCredit
              itemClassName="mb-2"
              creatorCredit={{
                creatorRolesString: project?.creatorRolesString,
                name: creative?.name || "",
                avatarInitials: creative?.avatarInitials || "",
                avatar80w: creative?.avatar80w || "",
                handle: creative?.handle || "",
              }}
            />
            {contributors?.map(edge => {
              const isCorrectType =
                edge?.node?.__typename === "CreativeWorkPortfolioProjectCreativeContributor" ||
                edge?.node?.__typename === "CreativeWorkPortfolioProjectWriteInContributor"
              const contributor = isCorrectType ? edge?.node : null

              return (
                <div key={edge?.node?.id}>
                  <CreativeCredit contributor={contributor} itemClassName="mb-2" />
                </div>
              )
            })}
          </div>
          {hasMoreThanFiveCredits && isAuthenticated && (
            <div className="group my-2 cursor-pointer py-2" onClick={() => setViewAllModalOpen(true)}>
              <Text
                variant="body3"
                className="text-fg-2 group-hover:text-fg-1">{`View all (${project?.contributors?.totalCount})`}</Text>
            </div>
          )}
          {Boolean(privateContributorsLength && privateContributorsLength > 0) && !isAuthenticated && (
            <div
              className="mt-3"
              onTouchStart={e => {
                e.stopPropagation()
                setShowMobileOverlay(true)
              }}>
              <PrivateUsersCard privateUsersLength={privateContributorsLength} />
            </div>
          )}
        </div>
        {showMobileOverlay && <MobileJoinNovaOverlay onDismiss={() => setShowMobileOverlay(false)} />}
      </MetadataSectionContainer>
    </>
  )
}
